import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { PathwayButton } from './PathwayButton';
export interface NamePathwayRequest {
  /** Dialog heading, e.g. "Name your pathway". */
  title: string;
  /** One-line explanation of what is about to be created. */
  description: React.ReactNode;
  /** Pre-filled value in the name field. */
  suggestedName: string;
  /** Label on the primary button. */
  confirmLabel: string;
}
interface NamePathwayModalProps {
  request: NamePathwayRequest | null;
  onConfirm: (name: string) => void;
  onCancel: () => void;
}

/**
 * Shared naming step for anything that creates a new pathway from an existing
 * one — duplicating a saved pathway, or starting from a template. Naming up
 * front stops the list filling with indistinguishable copies.
 */
export function NamePathwayModal({
  request,
  onConfirm,
  onCancel
}: NamePathwayModalProps) {
  const [name, setName] = useState('');
  useEffect(() => {
    if (request) setName(request.suggestedName);
  }, [request]);
  const trimmed = name.trim();
  return <AnimatePresence data-id="element-1624" data-mp="11x5di889vel8">
      {request && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.16
    }} className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 p-4" role="dialog" aria-modal="true" aria-labelledby="name-pathway-title" data-id="element-1625" data-mp="11ygg44pzegar">
          <motion.div initial={{
        opacity: 0,
        scale: 0.96,
        y: 8
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.96,
        y: 8
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} className="w-full max-w-md rounded-xl bg-white p-6 shadow-lg" data-id="element-1626" data-mp="1kwjbd194h9h0">
            <h2 id="name-pathway-title" className="text-lg font-bold text-gray-900" data-id="element-1627" data-mp="28lkdk6rd3odu">
              {request.title}
            </h2>
            <p className="mt-2 text-sm text-gray-600" data-id="element-1628" data-mp="2aqgyufd8ktxy">{request.description}</p>

            <label className="mt-5 block" data-id="element-1629" data-mp="2fnzbogudjtb8">
              <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-1630" data-mp="2fosa8uj6jcia">
                Pathway name
              </span>
              <input autoFocus value={name} onChange={e => setName(e.target.value)} onKeyDown={event => {
            if (event.key === 'Enter' && trimmed) {
              event.preventDefault();
              onConfirm(trimmed);
            }
            if (event.key === 'Escape') onCancel();
          }} className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#30718D]" data-id="element-1631" data-mp="2petv7dg8kesz" />
            </label>

            <div className="mt-6 grid grid-cols-2 gap-3" data-id="element-1632" data-mp="1dzoswak0oi9w">
              <PathwayButton variant="secondary" fullWidth onClick={onCancel} data-id="element-1633" data-mp="16g5t4smk0aem">
                Cancel
              </PathwayButton>
              <PathwayButton fullWidth onClick={() => onConfirm(trimmed)} disabled={trimmed.length === 0} data-id="element-1634" data-mp="11o5oimvjgtbp">
                {request.confirmLabel}
              </PathwayButton>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}